import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Market,  MarketInputTask, TaskType, StateAbbreviation } from '../../api/models/market-model';
import { MarketService } from '../../api/services/market.service';
import { Address } from '../../api/models/address';
import { AuthService } from '../../api/services/auth.service';
import { Team } from '../../api/models/team';
import { PreferencesService } from '../../api/services/preferences.service';
import { map } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { UserService } from '../../api/services/user.service';
import { User } from '../../api/models/user-model';
import { Account } from '../../api/models/account';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs';
import { RoleRequest } from '../../api/models/role-assignment-map';
import { Organization  } from '../../api/models/organization';

@Component({
  selector: 'app-market-edit',
  templateUrl: './market-edit.component.html',
  styleUrls: ['./market-edit.component.scss']
})


export class MarketEditComponent implements OnInit {

  marketForm: FormGroup;
  addressesForm: FormGroup;
  addressesFormArray: FormArray;
  taskForm: FormGroup;

  marketErrorMessages$: Subject<string[]> = new Subject();
  addressErrorMessages$: Subject<string[]> = new Subject();
  editMarketAddressForm = false;
  showEditMarketButton = false;
  marketAddressSaveRequired = false;
  isLoading = false;
  updatingMarketData: boolean = false;
  disableSaveButton: boolean = false;

  market: Market;
  addresses: Address[];
  marketInputTasks: MarketInputTask[];
  accounts$: Observable<Account>;
  selectedBillAccount: string;

  taskTypes: TaskType[] = [];
  selectedMarketTasks: string[] = [];

  addressIdsToRemove: string[] = [];

  action: string;

  orgId: string = '';

  defaultAddressId: string;
  defaultIndex: number;
  deletedAddIndex: number;
  addressDeleteError = false;

  initiators = [];
  tier1Approvers = [];
  tier2Approvers = [];

  teams: Team[] = [];
  users: User[] = [];
  teamListString;
  userListString;

  initiatorIds: string[] = [];
  types = [{display: 'Tower', value: 'Tower'}, {display: 'DAS', value: 'DAS'}, {display: 'Steel', value: 'Steel'}];
  selectedType: string;
  typeRequired = false;

  selectedAdminMailStop: string;
  specialFieldValue: string;

  existingInitiators = [];
  existingTier1Approvers = [];
  existingTier2Approvers = [];
  states: StateAbbreviation[] = [];

  roleRequest: RoleRequest;
  isTesscoAdmin: boolean = false;
  organizations: Organization[] = [];

  selectedSpecialField: string;

  specialFields: string[];
  isMarketEdit: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private marketService: MarketService,
    private authService: AuthService,
    private preferencesService: PreferencesService,
    private userService: UserService
  ) { }

  selectDefaultAddress(index: number) {
    const addressId = (this.addressesFormArray.at(index).value as Address).id;
    this.defaultAddressId = addressId;
    this.defaultIndex = index;
    this.preferencesService.setPreference('MARKET', this.market.id, 'default-address', { Value: this.defaultAddressId }).subscribe();
  }

  setClient(clientId: string) {
    this.orgId = clientId;
    this.market.customerId = clientId;
    this.preferencesService.getPreferences('CUSTOMER', clientId).subscribe(prefs => {
      this.specialFieldValue = prefs['special-fields'].Value ? prefs['special-fields'].Value : 'Standard';
      this.setValueForMarketType();
      this.fb.group({teamClientAdmin: [this.market.customerId]});
      if (this.specialFieldValue === 'Verizon eQuote' || this.specialFieldValue === 'Standard') {
        this.taskForm = this.fb.group({
          taskTypeForm: this.fb.array([])
        });
        this.getMarketTasks();
      }
    });
  }

  setTeams() {
    this.market.marketPermissions = [];
    this.initiators.forEach(initiator => {
      const type = initiator.isTeam ? 'USER_GROUP' : 'USER';
      this.market.marketPermissions.push({ roleType: '0', target: `${type}~${initiator.id}`, isAdmin: false });
    });
    this.tier1Approvers.forEach(approver1 => {
      const type = approver1.isTeam ? 'USER_GROUP' : 'USER';
      this.market.marketPermissions.push({ roleType: '1', target: `${type}~${approver1.id}`, isAdmin: false });
    });
    this.tier2Approvers.forEach(approver2 => {
      const type = approver2.isTeam ? 'USER_GROUP' : 'USER';
      this.market.marketPermissions.push({ roleType: '2', target: `${type}~${approver2.id}`, isAdmin: false });
    });
  }

  setMarketTasks() {
    this.marketInputTasks = [];
    const selectedOrderIds = this.taskForm.value.taskTypeForm
      .map((v, i) => v ? this.taskTypes[i].taskName : null)
      .filter(v => v !== null);
    //Approval is added as part of OASIS-1204
    if (this.specialFieldValue === 'Verizon eQuote' || this.specialFieldValue === 'Standard') {
      selectedOrderIds.push('APPROVAL');
    }else{
      this.market.marketInputTasks = [];
    }
    if (selectedOrderIds) {
      selectedOrderIds.forEach(selectedOrderId => {
        if (this.selectedMarketTasks.includes(selectedOrderId)) {
          const marketTask = this.market.marketTasks.find(mktTask => mktTask.marketTaskName.taskName === selectedOrderId);
          this.marketInputTasks.push(new MarketInputTask(marketTask.id, marketTask.marketTaskName.taskName));
        } else {
          this.marketInputTasks.push(new MarketInputTask(null, selectedOrderId))
        }
      });
    }
    this.market.marketInputTasks = this.marketInputTasks;
  }

  updateMarket(){
    if (this.marketAddressSaveRequired && this.addressesFormArray.value.length) {
      try {
        this.marketService.saveAddressList(this.market.id, this.addressesFormArray.value)
          .subscribe(() => {
            if (this.addressesFormArray.length === 1) {
              this.marketService.getAddresses(this.market.id).subscribe(result => {
                this.preferencesService.setPreference('MARKET', this.market.id, 'default-address', { Value: result[0].id }).subscribe();
              });
            }
          }, error => {
            this.updatingMarketData = false;
            this.addressErrorMessages$.next([error.error.message]);
          });
      } catch (error) {
        console.error('Address could not be saved', error);
      }
    }
    this.verifyWhetherMarketRolesModified();
    this.marketService.saveMarket(Object.assign(this.market, this.marketForm.value))
      .subscribe(() => {
        this.activeModal.close();
        this.updatingMarketData = false;
      }, error => {
        this.marketErrorMessages$.next([error.error.message]);
        this.updatingMarketData = false;
      });
  }

  createMarket(){
    this.marketService.saveMarket(Object.assign(this.market, this.marketForm.value))
    .subscribe((market: Market) => {
      this.updatingMarketData = false;
      if (this.addressesFormArray.value.length) {
        this.marketService.saveAddresses(market.id, this.addressesFormArray.value)
          .subscribe(() => {
            if (this.addressesFormArray.length === 1) {
              this.marketService.getAddresses(market.id).subscribe(result => {
                this.preferencesService.setPreference('MARKET', market.id, 'default-address', { Value: result[0].id }).subscribe();
              });
            }
            this.activeModal.close();
          }, error => {
            this.addressErrorMessages$.next([error.error.message]);
            this.updatingMarketData = false;
          });
      } else {
        this.activeModal.close();
      }
    }, error => {
      this.marketErrorMessages$.next([error.error.message]);
      this.updatingMarketData = false;
    });
  }

  save() {
    if (this.specialFieldValue === 'ATC' && !this.selectedType) {
      this.typeRequired = true;
      return;
    }
    if (this.checkDuplicateMarketAddress()) {
      this.updatingMarketData = true;
      this.setTeams();
      this.setMarketTasks();

      if (this.market.id) {
        this.updateMarket();
      } else {
        this.createMarket();
      }
    }
  }

  checkDuplicateMarketAddress(): boolean {
    var marketAddresses: Address[] = this.addressesFormArray.value;
    var marketNameIndexMap: Map<string, number> = new Map<string, number>();
    for (let index = 0; index < marketAddresses.length; index++) {
      if (marketNameIndexMap.has(marketAddresses[index].locationName.toLowerCase().trim())) {
        var errorIndex = marketNameIndexMap.get(marketAddresses[index].locationName.toLocaleLowerCase().trim()) + 1;
        this.addressErrorMessages$.next(["Duplicate Location Short Name at address record " + errorIndex + " and " + ++index]);
        return false;
      } else {
        marketNameIndexMap.set(marketAddresses[index].locationName.toLocaleLowerCase().trim(), index);
      }
    }
    return true;
  }

  private verifyWhetherMarketRolesModified() {

    let initiatorObjectsEqual: boolean = false;
    let tier1ApproversObjectsEqual: boolean = false;
    let tier2ApproversObjectsEqual: boolean = false;

    const objectsEqual = (o1, o2) => typeof o1 === 'object' && Object.keys(o1).length > 0
      ? Object.keys(o1).length === Object.keys(o2).length
      && Object.keys(o1).every(p => objectsEqual(o1[p], o2[p]))
      : o1 === o2;

    initiatorObjectsEqual = objectsEqual(this.existingInitiators, this.initiators);
    tier1ApproversObjectsEqual = objectsEqual(this.existingTier1Approvers, this.tier1Approvers);
    tier2ApproversObjectsEqual = objectsEqual(this.existingTier2Approvers, this.tier2Approvers);

    if (!initiatorObjectsEqual || !tier1ApproversObjectsEqual || !tier2ApproversObjectsEqual) {
      this.market.isMarketPermissionsModified = true;
    }
  }

  addAddress(): void {
    this.addressesFormArray.push(this.mapAddressToFormGroup(new Address()));
    if (this.addressesFormArray.length === 1) {
      this.defaultIndex = 0;
    }
    this.marketAddressSaveRequired = true;
  }

  rmAddress(index: number) {
    //edit for address
    this.deletedAddIndex = index;
    const addressId = (this.addressesFormArray.at(index).value as Address).id;
    if (addressId) {
      this.addressIdsToRemove.push(addressId);
      this.marketService.deleteAddresses(this.market.id, this.addressIdsToRemove)
      .subscribe(() => {
        this.addressesFormArray.removeAt(index);
        this.addressIdsToRemove = [];
      }, error => {
        this.addressErrorMessages$.next([error.error.message]);
      });
    } else {
      this.addressesFormArray.removeAt(index);
    }
  }

  haveTier2Conditions() {
    return this.marketForm.value.tier2ApprovalAmount || this.marketForm.value.triggerTier2ApprovalIfItemNotOnMasterCatalog;
  }

  private mapAddressToFormGroup(address: Address): FormGroup {
    if(address == null) {
      address = new Address();
    }
    return this.fb.group({
      id: address.id,
      locationName: [address.locationName || '', Validators.required],
      line1: [address.line1 || '', Validators.required],
      line2: [address.line2 || ''],
      line3: [address.line3 || ''],
      city: [address.city || '', Validators.required],
      state: [address.state || '', Validators.required],
      zip: [address.zip || '', Validators.required],
      contactName: [address.contactName || ''],
      contactPhone: [address.contactPhone || ''],
      acceptBulk: [address.acceptBulk || false],
      acceptParcel: [address.acceptParcel || false],
      requiresLiftGate: [address.requiresLiftGate || false],
      companyName: [address.companyName || '', Validators.required]
    });
  }

  autocompleteInitiators = (text: string) => {
    return this.authService.findTeamsAndUsersByOrganizationIdAndText(this.orgId, text)
      .pipe(map(data => data.filter(entity => entity.isTeam ? entity.teamType === 0 : entity)))
      .pipe(map(data => data.filter(entity => !entity.isAdmin)))
      .pipe(map(data => data.map(entity => {
        return { name: entity.fullname, id: entity.id, isTeam: entity.isTeam };
      })));
  }

  autocompleteApprovers = (text: string) => {
    return this.authService.findTeamsAndUsersByOrganizationIdAndText(this.orgId, text)
      .pipe(map(data => data.filter(entity => entity.isTeam ? entity.teamType === 1 : entity)))
      .pipe(map(data => data.filter(entity => !entity.isAdmin)))
      .pipe(map(data => data.map(entity => {
        return { name: entity.fullname, id: entity.id, isTeam: entity.isTeam };
      })));
  }

  getParcel(index: number) {
    return this.addressesFormArray.value[index].acceptParcel;
  }

  getBulk(index: number) {
    return this.addressesFormArray.value[index].acceptBulk;
  }

  getLiftGate(index: number) {
    return this.addressesFormArray.value[index].requiresLiftGate;
  }

  setParcel(index: number) {
    const form = this.addressesFormArray.controls[index] as FormGroup;
    form.controls['acceptParcel'].setValue(!this.getParcel(index));
  }

  setBulk(index: number) {
    const form = this.addressesFormArray.controls[index] as FormGroup;
    form.controls['acceptBulk'].setValue(!this.getBulk(index));
  }

  setLiftGate(index: number) {
    const form = this.addressesFormArray.controls[index] as FormGroup;
    form.controls['requiresLiftGate'].setValue(!this.getLiftGate(index));
  }

  getTargetId(target: string): string {
    return target.split('~')[1];
  }

  getTargetType(target: string): string {
    return target.split('~')[0];
  }

  setType(option) {
    this.selectedType = option;
    this.typeRequired = false;
  }

  fillInitiatorsAndApprovers() {
    this.market.marketPermissions.forEach(permission => {
      if (!permission.isAdmin) {
        const isTeam = this.getTargetType(permission.target) === 'USER_GROUP';
        let name;
        let temp;
        if (isTeam) {
          temp = this.teams.find(team => team.id === this.getTargetId(permission.target));
          name = temp ? temp.name : null;
        } else {
          temp = this.users.find(user => user['User ID'] === this.getTargetId(permission.target));
          name = temp ? temp.name : null;
        }
        if (name) {
          switch (permission.roleType) {
            case '0':
              this.initiators.push({ id: this.getTargetId(permission.target), name: name, isTeam: isTeam, display: name, value: this.getTargetId(permission.target) });
              break;
            case '1':
              this.tier1Approvers.push({ id: this.getTargetId(permission.target), name: name, isTeam: isTeam, display: name, value: this.getTargetId(permission.target) });
              break;
            case '2':
              this.tier2Approvers.push({ id: this.getTargetId(permission.target), name: name, isTeam: isTeam, display: name, value: this.getTargetId(permission.target) });
              break;
          }
        }
      }
    });
  }

  setMarketForm(){
    this.marketForm = this.fb.group({
      accountNumber: [this.market.accountNumber, Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(10)])],
      name: [this.market.name, Validators.required],
      description: [this.market.description, Validators.required],
      tier2ApprovalAmount: [this.market.tier2ApprovalAmount],
      generateReadoutReport: [this.market.generateReadoutReport],
      purchasePlanFlag: [this.market.id ? this.market.purchasePlanFlag : true],
      triggerTier2ApprovalIfItemNotOnMasterCatalog: [this.market.triggerTier2ApprovalIfItemNotOnMasterCatalog],
      type: [this.market.type],
      adminMailStopEmail: [this.market.adminMailStopEmail],
      associationCode: [this.market.associationCode, Validators.maxLength(10)],
      billingAddress: [this.market.billingAddress ? this.market.billingAddress : null, Validators.required],
      locationCode: [this.market.locationCode, Validators.compose([Validators.required, Validators.maxLength(10), Validators.minLength(10)])],
      teamClientAdmin: [this.market.customerId ? {value: this.market.customerId, disabled: true} : '']
    });
  }

  getMarketTasks(){
    this.marketService.getMarketTasks().subscribe(taskTypes => {
      this.taskTypes = taskTypes;
      if (this.taskTypes && this.taskTypes.length > 0) {
        for(let taskType of this.taskTypes){
          if(taskType.taskType === 'APPROVAL'){
            const index = this.taskTypes.indexOf(taskType, 0);
            if (index > -1) {
              this.taskTypes.splice(index, 1);
            }
            break;
          }
        }

        if (this.market.marketTasks && this.market.marketTasks.length > 0) {
          this.selectedMarketTasks = this.market.marketTasks.map(marketTask => {
            return marketTask.marketTaskName.taskName;
          });
          const ind = this.selectedMarketTasks.indexOf('APPROVAL', 0);
          if (ind > -1) {
            this.selectedMarketTasks.splice(ind, 1);
          }
        }
        this.taskTypes.forEach(taskType => {
          const control = this.fb.control(this.selectedMarketTasks.includes(taskType.taskName));
          (this.taskForm.controls.taskTypeForm as FormArray).push(control);
        });
      }
    });
  }

  setValueForMarketType() {
    if (this.market.typeOutput) {
      this.selectedType = this.market.typeOutput;
    } else {
      if (this.specialFieldValue === 'ATC') {
        this.typeRequired = true;
      }
    }
  }

  ngOnInit() {
    this.roleRequest = new RoleRequest('ADMIN_TESSCO', null);
    this.authService.getUserRoles(this.roleRequest).subscribe(roles => {
      this.isTesscoAdmin = roles.getRoleMap('ADMIN_TESSCO');
    });
    this.userService.getOrganizations().subscribe(orgs => {
      this.organizations = orgs;
      if(this.organizations.length ==1){this.setClient(this.organizations[0]['Organization ID']);}
    });
    this.setMarketForm();
    if(this.market.accountNumber) {
      this.isLoading = true;
      this.accounts$ = this.marketService.getAccount(this.market.accountNumber);
      this.disableSaveButton = true;
      this.accounts$.forEach(acc => {
        if (acc.bill != null && acc.bill.length) {
            this.disableSaveButton = false;
        }
      });
    }
    this.selectedBillAccount = this.marketForm.controls.billingAddress.value ? this.marketForm.controls.billingAddress.value["account"] : undefined;
    this.marketForm.controls.adminMailStopEmail.valueChanges
      .debounceTime(100)
      .distinctUntilChanged()
      .subscribe((change) => {
      if (change) {
        this.marketForm.controls.adminMailStopEmail.setValidators([Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')]);
        this.marketForm.controls['adminMailStopEmail'].updateValueAndValidity();
      } else {
        this.marketForm.controls.adminMailStopEmail.setErrors(null);
        this.marketForm.controls.adminMailStopEmail.clearValidators();
      }
    });
   
   
    this.taskForm = this.fb.group({
      taskTypeForm: this.fb.array([])
    });

    // if (this.specialFieldValue === 'Verizon eQuote' || this.specialFieldValue === 'Standard') {
    //   this.getMarketTasks();
    // }

    this.marketService.getStateAbbreviations().subscribe(response => {
      this.states = response;
    });

    this.addressesForm = this.fb.group({
      addresses: this.fb.array(this.addresses.map(a => this.mapAddressToFormGroup(a)))
    });
    this.addressesFormArray = this.addressesForm.get('addresses') as FormArray;

    if (this.addressesFormArray.length == 0) {
      this.editMarketAddressForm = true;
    }
   
    if (this.market.id) {
      this.setClient(this.market.customerId);
      this.isMarketEdit = true;
      this.showEditMarketButton = true;
      this.preferencesService.getPreferences('MARKET', this.market.id).subscribe(prefs => {
        this.defaultAddressId = prefs['default-address'].Value;
        this.addressesFormArray.controls.forEach((control, index) => {
          if (control.value.id === this.defaultAddressId) {
            this.defaultIndex = index;
          }
        });
      });
    } else {
      //condition for ADD
      this.showEditMarketButton = false;
    }

    this.orgId = this.market.customerId;

    this.getTeamsAndUsersList();
    this.setValueForMarketType();

    forkJoin(this.authService.getTeamsWithPermission(this.teamListString),
    this.userService.getUsersWithPermission(this.userListString)).subscribe(teamsAndUsers => {
      this.teams = teamsAndUsers[0];
      this.users = teamsAndUsers[1];
      this.fillInitiatorsAndApprovers();
      this.existingInitiators = this.initiators;
      this.existingTier1Approvers = this.tier1Approvers;
      this.existingTier2Approvers = this.tier2Approvers;
    });
  }

  getTeamsAndUsersList(){
    this.teamListString = '';
    this.userListString = '';

    this.market.marketPermissions.forEach(permission => {
      if (!permission.isAdmin) {
        const isTeam = this.getTargetType(permission.target) === 'USER_GROUP';
        (isTeam) ? this.teamListString += this.getTargetId(permission.target) + ',' : this.userListString += this.getTargetId(permission.target) + ',' ;
      }
    });
  }

  editMarketAddressDetails() {
    this.editMarketAddressForm = true;
    this.marketAddressSaveRequired = true;
  }

  getTesscoAccountNumberDetails() {
    var accountNumber = this.marketForm.controls.accountNumber.value;
    if (accountNumber && accountNumber.length == 10) {
      this.disableSaveButton = true;
      this.isLoading = true;
      this.accounts$ = this.marketService.getAccount(accountNumber);
      this.accounts$.forEach(acc => {
        if (acc.bill != null) {
          this.disableSaveButton = false;
          if (acc.bill.length == 1) {
            this.onselectingBillTo(acc.bill[0])
          }
        }
      });
    } else {
      this.disableSaveButton = true;
      this.accounts$ = undefined;
    }
  }

  onselectingBillTo(selectedAccount:any) {
    this.selectedBillAccount = selectedAccount.account;
    var billingAddressAssociatedWithMarket = {
      account: selectedAccount.account,
      name: selectedAccount.name,
      salesOrg: selectedAccount.salesOrg,
      distChannel: selectedAccount.distChannel,
      division: selectedAccount.division,
      city: selectedAccount.address.city,
      region: selectedAccount.address.region,
      country: selectedAccount.address.country,
      postalCode: selectedAccount.address.postalCode,
      street: selectedAccount.address.street
    };
    this.marketForm.patchValue({ billingAddress: billingAddressAssociatedWithMarket });
  }

  closeMarketErrorMessage() {
    this.marketErrorMessages$.next(undefined);
  }

  closeAddressErrorMessage() {
    this.addressErrorMessages$.next(undefined);
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/market-edit/market-edit.component.ts